import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShoppingBag } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useNavigate } from 'react-router-dom';
import CartItem from './CartItem';
import { PricingSummary } from './CartPricing';

const overlayMotion = { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } };
const panelMotion = {
  initial: { x: '100%' },
  animate: { x: 0 },
  exit: { x: '100%' },
  transition: { type: 'spring', damping: 25, stiffness: 200 },
};
const checkoutButtonMotion = { whileHover: { scale: 1.02 }, whileTap: { scale: 0.98 } };

const CartSidebar = () => {
  const { cart, isCartOpen, setIsCartOpen, getTotalPrice } = useCart();
  const navigate = useNavigate();

  const subtotal = getTotalPrice();

  const handleCheckout = () => {
    setIsCartOpen(false);
    navigate('/checkout');
  };

  return (
    <AnimatePresence>
      {isCartOpen && (
        <>
          <motion.div
            {...overlayMotion}
            className="fixed inset-0 bg-black/50 z-50"
            onClick={() => setIsCartOpen(false)}
            data-testid="cart-overlay"
          />
          <motion.div
            {...panelMotion}
            className="fixed right-0 top-0 h-full w-full max-w-md bg-white/95 backdrop-blur-xl border-l-4 border-orange-400 shadow-2xl z-50 flex flex-col"
            data-testid="cart-sidebar"
          >
            <div className="p-6 border-b border-orange-200 flex items-center justify-between">
              <h2 className="text-2xl font-bold heading-font" style={{ color: '#7C3AED' }}>Your Party Cart</h2>
              <button onClick={() => setIsCartOpen(false)} className="text-gray-500 hover:text-orange-500 transition-colors" data-testid="close-cart-button">
                <X size={28} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {cart.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center" data-testid="empty-cart-message">
                  <ShoppingBag size={80} className="text-gray-300 mb-4" />
                  <p className="text-xl text-gray-500">Your cart is empty</p>
                  <p className="text-sm text-gray-400 mt-2">Add some party items to get started!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {cart.map((item) => (
                    <CartItem key={item.id} item={item} />
                  ))}
                </div>
              )}
            </div>

            {cart.length > 0 && (
              <div className="p-6 border-t border-orange-200 bg-orange-50">
                <PricingSummary subtotal={subtotal} />

                <motion.button
                  {...checkoutButtonMotion}
                  onClick={handleCheckout}
                  className="w-full py-4 rounded-full font-bold text-lg shadow-lg transition-all bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white"
                  data-testid="checkout-button"
                >
                  Proceed to Order
                </motion.button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartSidebar;
